/**
 * Docs (eIFU) API client. All functions no-op when docs are disabled (REACT_APP_DOCS_API_URL not set).
 */
import { getToken } from "../../classes/utils/TokenUtils";
import {
  isDocsEnabled,
  getDocsEifuResolveUrl,
  getDocsSlaResolveUrl,
  getDocsApiBaseUrl,
  getEifuVersion,
} from "../../config/docsConfig";

/**
 * Resolve a document path from the API against the configured Docs base URL.
 * @param {string} url - Absolute URL or path (e.g. /api/docs/...).
 */
export const normalizeDocsFileUrl = url => {
  if (!url || typeof url !== "string") return url;
  const trimmed = url.trim();
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  const base = getDocsApiBaseUrl().replace(/\/$/, "");
  const path = trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
  return `${base}${path}`;
};

/**
 * Download a document from the Docs API using the SPA session token.
 * Browsers do not send Authorization on iframe navigations or plain window.open URLs — use the blob with createObjectURL instead.
 */
export const fetchDocsFileBlob = async documentUrl => {
  const resolved = normalizeDocsFileUrl(documentUrl);
  const token = getToken();
  if (!token) throw new Error("Not authenticated");

  const res = await fetch(resolved, {
    headers: { Authorization: `Bearer ${token}` },
    credentials: "omit",
  });
  if (res.status === 401) throw new Error("Session expired or unauthorized");
  if (!res.ok) throw new Error(`Document fetch failed: ${res.status}`);
  return res.blob();
};

/**
 * Resolve eIFU document(s) for the given app version.
 * @param {string} [version] - App version (e.g. from process.env.REACT_APP_VERSION). Optional.
 * @returns {Promise<{ files?: Array<{ url: string, name?: string }>, error?: string }>}
 *   When docs disabled: { files: [] }. On success: { files }. On failure: { error } (caller can fallback to placeholder).
 */
async function resolveDocsByUrl(getResolveUrl, version) {
  if (!isDocsEnabled()) {
    return { files: [] };
  }

  const url = getResolveUrl(version);
  if (!url) return { files: [] };

  const token = getToken();
  const headers = {
    Accept: "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  try {
    const res = await fetch(url, {
      method: "GET",
      headers,
      credentials: "omit",
    });

    if (!res.ok) {
      if (res.status === 404) return { files: [] };
      return { error: `Docs API returned ${res.status}` };
    }

    const data = await res.json();
    const raw = Array.isArray(data?.files)
      ? data.files
      : data?.Files
        ? Array.isArray(data.Files)
          ? data.Files
          : [data.Files]
        : data?.files
          ? [data.files]
          : [];
    const files = raw.map(f => ({
      url: f?.url ?? f?.Url,
      name: f?.name ?? f?.Name,
    }));
    return { files };
  } catch (err) {
    return { error: err.message || "Failed to load documentation" };
  }
}

/**
 * Resolve eIFU document(s) for the given app version.
 * @param {string} [version]
 */
export const resolveEifu = version =>
  resolveDocsByUrl(getDocsEifuResolveUrl, version);

/**
 * Resolve SLA document(s) for the given app version (same version query as eIFU; blob type SLA/).
 * @param {string} [version]
 */
export const resolveSla = version =>
  resolveDocsByUrl(getDocsSlaResolveUrl, version);

/**
 * Fetches the first file from a resolve helper and opens it in a new tab (blob URL; auth header not sent on plain navigation).
 * @param {(version: string) => Promise<{ files?: Array<{ url: string }>, error?: string }>} resolveFn - e.g. resolveEifu or resolveSla
 */
export const openDocsResolvedPdfInNewTab = async resolveFn => {
  if (!isDocsEnabled()) return;
  const version = getEifuVersion() || "";
  const { files } = await resolveFn(version);
  const firstUrl = files?.[0]?.url;
  if (!firstUrl) return;
  try {
    const blob = await fetchDocsFileBlob(firstUrl);
    const blobUrl = URL.createObjectURL(blob);
    window.open(blobUrl, "_blank", "noopener,noreferrer");
  } catch (e) {
    console.error("Failed to open document in new tab", e);
  }
};
